import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  X,
  CheckCircle,
  XCircle,
  Link as LinkIcon,
  Activity,
  MessageSquare,
  Shuffle,
  Crown,
  Gift,
  Shield,
  Tag,
  Search,
  Loader2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { getSupabaseAdminClient, Profile, PartnerConnection } from '@parentpath/supabase-client';
import { formatPremiumSource } from './utils/premiumSourceLabels';
import { callAdminUsers, describeBanError } from './utils/adminUsersClient';

const supabase = getSupabaseAdminClient();

interface UserDetailModalProps {
  userId: string;
  onClose: () => void;
}

type TabType = 'profile' | 'partner' | 'activity' | 'premium';

export function UserDetailModal({ userId, onClose }: UserDetailModalProps) {
  const [activeTab, setActiveTab] = useState<TabType>('profile');
  const queryClient = useQueryClient();

  // Fetch user profile
  const { data: user, isLoading: userLoading } = useQuery({
    queryKey: ['user-detail', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) throw error;
      return data as Profile;
    },
  });

  // Fetch partner connection
  const { data: partnerConnection } = useQuery({
    queryKey: ['user-partner-connection', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('partner_connections')
        .select('*, user_1:profiles!partner_connections_user_1_id_fkey(id, email, display_name), user_2:profiles!partner_connections_user_2_id_fkey(id, email, display_name)')
        .or(`user_1_id.eq.${userId},user_2_id.eq.${userId}`)
        .eq('status', 'active')
        .maybeSingle();

      if (error) throw error;
      return data as (PartnerConnection & {
        user_1: Pick<Profile, 'id' | 'email' | 'display_name'>;
        user_2: Pick<Profile, 'id' | 'email' | 'display_name'>;
      }) | null;
    },
  });

  // Fetch last activity timestamp (from activity_log)
  const { data: lastActivity } = useQuery({
    queryKey: ['user-last-activity', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('activity_log')
        .select('created_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) return null;
      return (data as { created_at: string } | null)?.created_at || null;
    },
  });

  // Fetch code redemption (Lifetime-Cap-Eintrag, falls vorhanden)
  const { data: redemption, refetch: refetchRedemption } = useQuery({
    queryKey: ['user-code-redemption', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('code_redemptions')
        .select('id, code, code_type, redeemed_at, premium_expires_at, is_retroactive, invitation_id, beta_invitations:invitation_id(note)')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching redemption:', error);
        return null;
      }
      return data as {
        id: string;
        code: string;
        code_type: 'beta' | 'promo';
        redeemed_at: string;
        premium_expires_at: string | null;
        is_retroactive: boolean;
        invitation_id: string;
        beta_invitations: { note: string | null } | null;
      } | null;
    },
  });

  // Fetch activity log entries for user
  const { data: activityLogEntries } = useQuery({
    queryKey: ['user-activity-log', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('activity_log')
        .select('id, action, resource_type, resource_id, metadata, created_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) return [];
      return data as Array<{
        id: string;
        action: string;
        resource_type: string | null;
        resource_id: string | null;
        metadata: Record<string, unknown> | null;
        created_at: string;
      }>;
    },
  });

  // Fetch activity stats
  const { data: activityStats } = useQuery({
    queryKey: ['user-activity', userId],
    queryFn: async () => {
      const [swipesResult, aiChatsResult, checklistsResult] = await Promise.all([
        supabase
          .from('name_swipes')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', userId),
        supabase
          .from('ai_conversations')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', userId),
        supabase
          .from('user_checklist_items')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', userId)
          .eq('is_completed', true),
      ]);

      return {
        totalSwipes: swipesResult.count || 0,
        totalAiChats: aiChatsResult.count || 0,
        completedChecklists: checklistsResult.count || 0,
      };
    },
  });

  // Ban/Unban mutation
  //
  // Wie in UsersPage ueber die Edge Function: die Sperre wirkt erst, wenn sie
  // auch in auth.users steht, und die Sperrspalten in profiles sind seit
  // Migration 168 gegen direkte Schreibzugriffe geschuetzt.
  const banMutation = useMutation({
    mutationFn: async (shouldBan: boolean) => {
      await callAdminUsers<{ success: boolean; user_id: string; banned: boolean }>({
        action: 'ban',
        user_id: userId,
        banned: shouldBan,
      });
    },
    onSuccess: (_, shouldBan) => {
      toast.success(shouldBan ? 'Benutzer wurde gesperrt' : 'Benutzer wurde entsperrt');
      queryClient.invalidateQueries({ queryKey: ['user-detail', userId] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error: Error) => {
      toast.error(describeBanError(error.message));
    },
  });

  // Disconnect partner mutation
  const disconnectPartnerMutation = useMutation({
    mutationFn: async () => {
      if (!partnerConnection) throw new Error('Keine Verbindung vorhanden');

      const { error } = await supabase
        .from('partner_connections')
        .update({
          status: 'disconnected',
          disconnected_at: new Date().toISOString(),
        } as never)
        .eq('id', partnerConnection.id);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Partner-Verbindung wurde getrennt');
      queryClient.invalidateQueries({ queryKey: ['user-partner-connection', userId] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: () => {
      toast.error('Fehler beim Trennen der Verbindung');
    },
  });

  // Generate a random beta code
  const generateBetaCode = (): string => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = 'BETA-';
    for (let i = 0; i < 8; i++) {
      if (i === 4) code += '-';
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  // Beta access mutation - auto-generates code when granting
  const betaMutation = useMutation({
    mutationFn: async (grantBeta: boolean) => {
      if (grantBeta) {
        // Generate and create a new beta code for the user
        const newCode = generateBetaCode();
        const { data: adminUser } = await supabase.auth.getUser();

        // Create beta invitation record
        const { error: inviteError } = await supabase
          .from('beta_invitations')
          .insert({
            code: newCode,
            note: `Manuell gewährt für ${user?.email}`,
            max_uses: 1,
            current_uses: 1,
            is_active: false, // Already used
            created_by: adminUser?.user?.id || null,
            used_by: userId,
            used_at: new Date().toISOString(),
          } as never);

        if (inviteError) throw inviteError;

        // Update user profile with beta status and generated code
        const { error } = await supabase
          .from('profiles')
          .update({
            is_beta_tester: true,
            subscription_tier: 'premium',
            beta_granted_at: new Date().toISOString(),
            beta_invitation_code: newCode,
            beta_activated_at: new Date().toISOString(),
          } as never)
          .eq('id', userId);

        if (error) throw error;
      } else {
        // Revoke beta access
        const { error } = await supabase
          .from('profiles')
          .update({
            is_beta_tester: false,
            subscription_tier: 'free',
            beta_granted_at: null,
          } as never)
          .eq('id', userId);

        if (error) throw error;
      }
    },
    onSuccess: (_, grantBeta) => {
      toast.success(grantBeta ? 'Beta-Zugang gewährt' : 'Beta-Zugang entzogen');
      queryClient.invalidateQueries({ queryKey: ['user-detail', userId] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['beta-codes'] });
    },
    onError: () => {
      toast.error('Fehler beim Aktualisieren des Beta-Status');
    },
  });

  // Free Premium mutation
  const [premiumMonths, setPremiumMonths] = useState(1);
  const grantPremiumMutation = useMutation({
    mutationFn: async (months: number) => {
      const expiresAt = new Date();
      expiresAt.setMonth(expiresAt.getMonth() + months);

      const { error } = await supabase
        .from('profiles')
        .update({
          subscription_tier: 'premium',
          subscription_status: 'active',
          premium_source: 'admin_grant',
          premium_expires_at: expiresAt.toISOString(),
          free_premium_until: expiresAt.toISOString(),
          free_premium_granted_at: new Date().toISOString(),
          free_premium_months: months,
        } as never)
        .eq('id', userId);

      if (error) throw error;
    },
    onSuccess: (_, months) => {
      toast.success(`${months} Monat(e) kostenloses Premium gewährt`);
      queryClient.invalidateQueries({ queryKey: ['user-detail', userId] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: () => {
      toast.error('Fehler beim Gewähren von Premium');
    },
  });

  // Revoke free premium
  const revokePremiumMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('profiles')
        .update({
          subscription_tier: user?.is_beta_tester ? 'premium' : 'free',
          subscription_status: user?.stripe_subscription_id ? 'active' : null,
          premium_source: user?.is_beta_tester ? 'beta_code' : null,
          premium_expires_at: null,
          free_premium_until: null,
          free_premium_granted_at: null,
          free_premium_months: null,
        } as never)
        .eq('id', userId);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Kostenloses Premium entzogen');
      queryClient.invalidateQueries({ queryKey: ['user-detail', userId] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: () => {
      toast.error('Fehler beim Entziehen von Premium');
    },
  });

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatRelativeDate = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (hours < 1) return 'gerade eben';
    if (hours < 24) return `vor ${hours} Stunden`;
    if (days === 1) return 'gestern';
    if (days < 7) return `vor ${days} Tagen`;
    return formatDate(dateString);
  };

  const getPartnerInfo = () => {
    if (!partnerConnection) return null;
    if (partnerConnection.user_1_id === userId) {
      return partnerConnection.user_2;
    }
    return partnerConnection.user_1;
  };

  const partner = getPartnerInfo();

  const tabs: { id: TabType; label: string }[] = [
    { id: 'profile', label: 'Profil' },
    { id: 'premium', label: 'Premium' },
    { id: 'partner', label: 'Partner' },
    { id: 'activity', label: 'Aktivität' },
  ];

  // Premium status helpers
  const hasPaidSubscription = !!(user?.stripe_subscription_id && user?.subscription_status === 'active');
  const hasFreePremium = !!(user?.free_premium_until && new Date(user.free_premium_until) > new Date());
  const isBetaTester = !!user?.is_beta_tester;
  const isPremium = user?.subscription_tier === 'premium';
  const premiumSourceLabel = user ? formatPremiumSource(user) : 'Kein Premium';
  const isIapUser = user?.premium_source === 'apple' || user?.premium_source === 'google';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50" style={{ backdropFilter: 'blur(2px)' }}>
      <div className="fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-xl overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              {userLoading ? (
                <div className="animate-pulse">
                  <div className="h-8 w-48 bg-slate-200 rounded mb-2"></div>
                  <div className="h-4 w-32 bg-slate-200 rounded"></div>
                </div>
              ) : (
                <>
                  <h2 className="text-2xl font-bold text-slate-900">
                    {user?.display_name || 'Unbenannt'}
                  </h2>
                  <p className="text-slate-600">{user?.email}</p>
                </>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-100 rounded-lg"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Tabs */}
          <div className="mb-6 border-b border-slate-200">
            <div className="flex gap-4">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 font-medium ${
                    activeTab === tab.id
                      ? 'border-b-2 border-teal-500 text-teal-600'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="space-y-6">
            {activeTab === 'profile' && user && (
              <>
                <div>
                  <h3 className="text-lg font-semibold text-slate-800 mb-3">Persönliche Daten</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">Name</span>
                      <span className="text-sm font-medium">{user.display_name || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">E-Mail</span>
                      <span className="text-sm font-medium">{user.email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">Geburtsdatum</span>
                      <span className="text-sm font-medium">{formatDate(user.birth_date)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">Geschlecht</span>
                      <span className="text-sm font-medium">
                        {user.gender === 'female' ? 'Weiblich' :
                         user.gender === 'male' ? 'Männlich' :
                         user.gender === 'other' ? 'Divers' : '-'}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-800 mb-3">Account</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">Registriert am</span>
                      <span className="text-sm font-medium">{formatDate(user.created_at)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">Letzte Aktivität</span>
                      <span className="text-sm font-medium">{formatRelativeDate(lastActivity || user.last_login_at)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">E-Mail verifiziert</span>
                      <span className="text-sm font-medium text-emerald-600">
                        <CheckCircle className="w-4 h-4 inline mr-1" />
                        Ja
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-600">Rolle</span>
                      {user.user_role === 'pregnant' ? (
                        <span className="px-2 py-1 bg-rose-100 text-rose-700 text-xs rounded-full">
                          Schwangere
                        </span>
                      ) : user.user_role === 'partner' ? (
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                          Partner
                        </span>
                      ) : user.role === 'admin' ? (
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                          Admin
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded-full">
                          Benutzer
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {user.is_banned && (
                  <div className="p-4 bg-red-50 rounded-lg">
                    <h3 className="text-lg font-semibold text-red-800 mb-2 flex items-center gap-2">
                      <XCircle className="w-5 h-5" />
                      Account gesperrt
                    </h3>
                    <div className="space-y-1 text-sm">
                      <p><span className="text-red-600">Gesperrt am:</span> {formatDate(user.banned_at)}</p>
                      <p><span className="text-red-600">Grund:</span> {user.banned_reason || '-'}</p>
                    </div>
                  </div>
                )}
              </>
            )}

            {activeTab === 'premium' && user && (
              <div className="space-y-6">
                {/* Premium Status Overview */}
                <div className={`flex items-center gap-4 p-4 rounded-lg ${
                  isPremium ? 'bg-amber-50' : 'bg-slate-50'
                }`}>
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                    isPremium ? 'bg-amber-100' : 'bg-slate-200'
                  }`}>
                    <Crown className={`w-8 h-8 ${isPremium ? 'text-amber-600' : 'text-slate-400'}`} />
                  </div>
                  <div>
                    <div className="text-xl font-bold text-slate-900">
                      {isPremium ? 'Premium Aktiv' : 'Free'}
                    </div>
                    <div className="text-sm text-slate-600">
                      {premiumSourceLabel}
                    </div>
                  </div>
                </div>

                {/* Premium-Quelle */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h3 className="text-sm font-semibold text-slate-700 mb-2">Premium-Quelle</h3>
                  <div className="text-base font-medium text-slate-900">{premiumSourceLabel}</div>
                  {user.premium_expires_at && (
                    <div className="text-sm text-slate-500 mt-1">
                      Premium läuft bis {formatDate(user.premium_expires_at)}
                    </div>
                  )}
                  {isIapUser && user.subscription_period_end && (
                    <div className="text-sm text-slate-500 mt-1">
                      Abrechnungsperiode bis {formatDate(user.subscription_period_end)}
                    </div>
                  )}
                  {isIapUser && user.revenuecat_product_id && (
                    <div className="text-sm text-slate-500 mt-1">
                      Produkt-ID: <span className="font-mono">{user.revenuecat_product_id}</span>
                    </div>
                  )}
                </div>

                {/* Code-Einlösung (für promo_code / beta_code) */}
                {redemption && (
                  <div className="p-4 bg-amber-50 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Tag className="w-5 h-5 text-amber-600" />
                        <h3 className="font-semibold text-amber-800">Eingelöster Code</h3>
                      </div>
                      {redemption.is_retroactive && (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                          Nachträglich verknüpft
                        </span>
                      )}
                    </div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-amber-700">Code:</span>
                        <span className="font-mono font-medium text-amber-900">{redemption.code}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-amber-700">Typ:</span>
                        <span className="text-amber-900">{redemption.code_type === 'beta' ? 'Beta-Code' : 'Promo-Code'}</span>
                      </div>
                      {redemption.beta_invitations?.note && (
                        <div className="flex justify-between">
                          <span className="text-amber-700">Notiz:</span>
                          <span className="text-amber-900">{redemption.beta_invitations.note}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-amber-700">Eingelöst am:</span>
                        <span className="text-amber-900">{formatDate(redemption.redeemed_at)}</span>
                      </div>
                      {redemption.premium_expires_at && (
                        <div className="flex justify-between">
                          <span className="text-amber-700">Läuft ab am:</span>
                          <span className="text-amber-900">{formatDate(redemption.premium_expires_at)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* "Code nachträglich verknüpfen" Button (nur wenn noch keine Redemption) */}
                {!redemption && (
                  <LinkRedemptionSection
                    userId={userId}
                    onLinked={() => {
                      refetchRedemption();
                      queryClient.invalidateQueries({ queryKey: ['user-detail', userId] });
                    }}
                  />
                )}

                {/* Subscription Details */}
                <div>
                  <h3 className="text-lg font-semibold text-slate-800 mb-3">Abo-Details</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">Status</span>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        isPremium
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-slate-100 text-slate-700'
                      }`}>
                        {isPremium ? 'Aktiv' : 'Inaktiv'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">Tier</span>
                      <span className="text-sm font-medium">{user.subscription_tier || 'free'}</span>
                    </div>
                    {hasPaidSubscription && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-sm text-slate-600">Stripe Subscription ID</span>
                          <span className="text-sm font-mono text-xs">{user.stripe_subscription_id}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-slate-600">Stripe Customer ID</span>
                          <span className="text-sm font-mono text-xs">{user.stripe_customer_id || '-'}</span>
                        </div>
                      </>
                    )}
                    {hasFreePremium && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-sm text-slate-600">Kostenlos bis</span>
                          <span className="text-sm font-medium">{formatDate(user.free_premium_until)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-slate-600">Gewährt am</span>
                          <span className="text-sm font-medium">{formatDate(user.free_premium_granted_at)}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Beta Access */}
                <div className="p-4 bg-purple-50 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-purple-600" />
                      <h3 className="font-semibold text-purple-800">Beta-Zugang</h3>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      isBetaTester
                        ? 'bg-purple-200 text-purple-800'
                        : 'bg-slate-200 text-slate-600'
                    }`}>
                      {isBetaTester ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </div>

                  {/* Beta Code Info */}
                  {isBetaTester && (
                    <div className="mb-3 space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-purple-600">Genutzter Code:</span>
                        <span className="font-mono font-medium text-purple-800">
                          {user.beta_invitation_code || 'Manuell gewährt'}
                        </span>
                      </div>
                      {user.beta_activated_at && (
                        <div className="flex justify-between text-sm">
                          <span className="text-purple-600">Aktiviert am:</span>
                          <span className="text-purple-800">{formatDate(user.beta_activated_at)}</span>
                        </div>
                      )}
                      {user.beta_granted_at && !user.beta_invitation_code && (
                        <div className="flex justify-between text-sm">
                          <span className="text-purple-600">Gewährt am:</span>
                          <span className="text-purple-800">{formatDate(user.beta_granted_at)}</span>
                        </div>
                      )}
                    </div>
                  )}

                  <p className="text-sm text-purple-700 mb-3">
                    Beta-Tester erhalten vollen Premium-Zugang während der Testphase.
                  </p>
                  <button
                    onClick={() => betaMutation.mutate(!isBetaTester)}
                    disabled={betaMutation.isPending}
                    className={`px-4 py-2 rounded-lg font-medium text-sm w-full ${
                      isBetaTester
                        ? 'bg-purple-200 hover:bg-purple-300 text-purple-800'
                        : 'bg-purple-600 hover:bg-purple-700 text-white'
                    } disabled:opacity-50`}
                  >
                    {betaMutation.isPending
                      ? 'Lädt...'
                      : isBetaTester
                      ? 'Beta-Zugang entziehen'
                      : 'Beta-Zugang gewähren'}
                  </button>
                </div>

                {/* Free Premium Grant */}
                <div className="p-4 bg-emerald-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <Gift className="w-5 h-5 text-emerald-600" />
                    <h3 className="font-semibold text-emerald-800">Kostenloses Premium</h3>
                  </div>
                  <p className="text-sm text-emerald-700 mb-3">
                    Gewähre kostenloses Premium für eine bestimmte Anzahl Monate.
                  </p>

                  {hasFreePremium ? (
                    <button
                      onClick={() => revokePremiumMutation.mutate()}
                      disabled={revokePremiumMutation.isPending}
                      className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg font-medium text-sm w-full disabled:opacity-50"
                    >
                      {revokePremiumMutation.isPending ? 'Lädt...' : 'Kostenloses Premium entziehen'}
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <select
                        value={premiumMonths}
                        onChange={(e) => setPremiumMonths(Number(e.target.value))}
                        className="flex-1 px-3 py-2 border border-emerald-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                      >
                        <option value={1}>1 Monat</option>
                        <option value={3}>3 Monate</option>
                        <option value={6}>6 Monate</option>
                        <option value={12}>12 Monate</option>
                      </select>
                      <button
                        onClick={() => grantPremiumMutation.mutate(premiumMonths)}
                        disabled={grantPremiumMutation.isPending || hasPaidSubscription}
                        className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium text-sm disabled:opacity-50"
                      >
                        {grantPremiumMutation.isPending ? 'Lädt...' : 'Gewähren'}
                      </button>
                    </div>
                  )}

                  {hasPaidSubscription && (
                    <p className="text-xs text-emerald-600 mt-2">
                      Benutzer hat bereits ein bezahltes Abo.
                    </p>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'partner' && (
              <>
                {partner ? (
                  <div className="space-y-6">
                    <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-lg">
                      <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                        <LinkIcon className="w-8 h-8 text-blue-600" />
                      </div>
                      <div>
                        <div className="text-xl font-bold text-slate-900">
                          {partner.display_name || 'Unbenannt'}
                        </div>
                        <div className="text-sm text-slate-600">{partner.email}</div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold text-slate-800 mb-3">Verbindungsdetails</h3>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-slate-600">Verbunden seit</span>
                          <span className="text-sm font-medium">{formatDate(partnerConnection?.connected_at || partnerConnection?.created_at || null)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-slate-600">Status</span>
                          <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                            Aktiv
                          </span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        if (confirm('Bist du sicher, dass du die Partner-Verbindung trennen möchtest?')) {
                          disconnectPartnerMutation.mutate();
                        }
                      }}
                      disabled={disconnectPartnerMutation.isPending}
                      className="w-full px-4 py-2 bg-rose-100 hover:bg-rose-200 text-rose-700 rounded-lg font-medium text-sm disabled:opacity-50 transition-colors"
                    >
                      {disconnectPartnerMutation.isPending ? 'Wird getrennt...' : 'Verbindung trennen'}
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <LinkIcon className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">Keine Partner-Verbindung</p>
                  </div>
                )}
              </>
            )}

            {activeTab === 'activity' && (
              <div className="space-y-6">
                {activityStats && (
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 bg-purple-50 rounded-lg text-center">
                      <Shuffle className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                      <div className="text-2xl font-bold text-slate-900">{activityStats.totalSwipes}</div>
                      <div className="text-sm text-slate-600">Name-Swipes</div>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg text-center">
                      <MessageSquare className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                      <div className="text-2xl font-bold text-slate-900">{activityStats.totalAiChats}</div>
                      <div className="text-sm text-slate-600">KI-Chats</div>
                    </div>
                    <div className="p-4 bg-emerald-50 rounded-lg text-center">
                      <CheckCircle className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                      <div className="text-2xl font-bold text-slate-900">{activityStats.completedChecklists}</div>
                      <div className="text-sm text-slate-600">Checklisten</div>
                    </div>
                  </div>
                )}

                <div>
                  <h3 className="text-lg font-semibold text-slate-800 mb-3">Aktivitätsprotokoll</h3>
                  {activityLogEntries && activityLogEntries.length > 0 ? (
                    <div className="max-h-[400px] overflow-y-auto space-y-2">
                      {activityLogEntries.map((entry) => (
                        <div key={entry.id} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                          <div className="w-2 h-2 rounded-full bg-teal-500 mt-1.5 shrink-0"></div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-sm font-medium text-slate-900 truncate">
                                {entry.action}
                              </span>
                              <span className="text-xs text-slate-500 whitespace-nowrap">
                                {formatRelativeDate(entry.created_at)}
                              </span>
                            </div>
                            {entry.resource_type && (
                              <div className="text-xs text-slate-500 mt-0.5">
                                {entry.resource_type}{entry.resource_id ? ` #${entry.resource_id.slice(0, 8)}` : ''}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <Activity className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                      <p>Keine Aktivitäten protokolliert</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          {/* E-Mail und Loeschen liegen in der Aktionen-Spalte der Uebersicht.
              Hier steht nur das Sperren, weil es sich auf den gerade
              geoeffneten Account bezieht. */}
          <div className="mt-8 pt-6 border-t border-slate-200 flex gap-3">
            <button
              onClick={() => banMutation.mutate(!user?.is_banned)}
              disabled={banMutation.isPending}
              className={`flex-1 px-4 py-2 ${
                user?.is_banned
                  ? 'bg-emerald-500 hover:bg-emerald-600'
                  : 'bg-rose-500 hover:bg-rose-600'
              } text-white rounded-lg font-medium disabled:opacity-50`}
            >
              {banMutation.isPending
                ? 'Lädt...'
                : user?.is_banned
                ? 'Account entsperren'
                : 'Account sperren'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface LinkRedemptionSectionProps {
  userId: string;
  onLinked: () => void;
}

interface InvitationSearchResult {
  id: string;
  code: string;
  code_type: 'beta' | 'promo';
  note: string | null;
  is_active: boolean;
  current_uses: number;
  max_uses: number | null;
}

function LinkRedemptionSection({ userId, onLinked }: LinkRedemptionSectionProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInvitationId, setSelectedInvitationId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: searchResults, isLoading } = useQuery({
    queryKey: ['beta-invitations-search', searchTerm],
    queryFn: async () => {
      const trimmed = searchTerm.trim();
      if (trimmed.length < 2) return [] as InvitationSearchResult[];

      const { data, error } = await supabase
        .from('beta_invitations')
        .select('id, code, code_type, note, is_active, current_uses, max_uses')
        .or(`code.ilike.%${trimmed}%,note.ilike.%${trimmed}%`)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) {
        console.error('Search error:', error);
        return [] as InvitationSearchResult[];
      }
      return (data ?? []) as InvitationSearchResult[];
    },
    enabled: isOpen && searchTerm.trim().length >= 2,
  });

  const handleLink = async () => {
    if (!selectedInvitationId) return;
    setIsSubmitting(true);
    try {
      // supabase-js v2 RPC type inference works inconsistently with our Schema typing,
      // so we cast args via `unknown` to a Record matching the SQL function signature.
      const { data, error } = await (supabase.rpc as unknown as (
        fn: 'link_redemption_for_user',
        args: { p_user_id: string; p_invitation_id: string },
      ) => Promise<{ data: unknown; error: { message: string; code?: string } | null }>)(
        'link_redemption_for_user',
        {
          p_user_id: userId,
          p_invitation_id: selectedInvitationId,
        },
      );

      if (error) {
        toast.error(error.message || 'Fehler beim Verknüpfen');
        return;
      }

      const result = data as { success: boolean; error?: string; error_code?: string };
      if (!result.success) {
        toast.error(result.error || 'Verknüpfung fehlgeschlagen');
        return;
      }

      toast.success('Code erfolgreich verknüpft');
      setIsOpen(false);
      setSearchTerm('');
      setSelectedInvitationId(null);
      onLinked();
    } catch (err) {
      console.error('Link error:', err);
      toast.error('Fehler beim Verknüpfen');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) {
    return (
      <div className="p-4 bg-slate-50 rounded-lg">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-700">Code nachträglich verknüpfen</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Falls der User seinen Code im alten Flow eingelöst hat oder als manuelle Korrektur.
            </p>
          </div>
          <button
            onClick={() => setIsOpen(true)}
            className="px-3 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg font-medium text-sm flex items-center gap-1"
          >
            <Tag className="w-4 h-4" />
            Verknüpfen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-slate-50 rounded-lg space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700">Code suchen und verknüpfen</h3>
        <button
          onClick={() => {
            setIsOpen(false);
            setSearchTerm('');
            setSelectedInvitationId(null);
          }}
          className="text-slate-400 hover:text-slate-600"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Code oder Notiz (min. 2 Zeichen)"
          className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
        />
      </div>
      {isLoading && (
        <div className="text-center py-4 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
          Suche...
        </div>
      )}
      {!isLoading && searchTerm.trim().length >= 2 && searchResults && searchResults.length === 0 && (
        <div className="text-center py-4 text-sm text-slate-500">Keine Codes gefunden</div>
      )}
      {!isLoading && searchResults && searchResults.length > 0 && (
        <div className="max-h-60 overflow-y-auto space-y-1">
          {searchResults.map((inv) => (
            <button
              key={inv.id}
              onClick={() => setSelectedInvitationId(inv.id)}
              className={`w-full text-left p-2 rounded border transition-colors ${
                selectedInvitationId === inv.id
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-slate-200 hover:bg-slate-100'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm font-medium">{inv.code}</span>
                <span className={`px-2 py-0.5 text-xs rounded-full ${
                  inv.code_type === 'promo'
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-purple-100 text-purple-700'
                }`}>
                  {inv.code_type === 'promo' ? 'Promo' : 'Beta'}
                </span>
                <span className="text-xs text-slate-500">
                  {inv.current_uses} / {inv.max_uses ?? '∞'}
                </span>
              </div>
              {inv.note && (
                <div className="text-xs text-slate-500 mt-0.5 truncate">{inv.note}</div>
              )}
            </button>
          ))}
        </div>
      )}
      <button
        onClick={handleLink}
        disabled={!selectedInvitationId || isSubmitting}
        className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium text-sm disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
        Verknüpfen
      </button>
    </div>
  );
}
